import { diffArrays, diffSentences } from 'diff';
function normalizeValue(value) {
    if (Array.isArray(value)) {
        return value.map(normalizeValue);
    }
    if (value !== null && typeof value === 'object') {
        const normalized = {};
        for (const key of Object.keys(value).sort()) {
            normalized[key] = normalizeValue(value[key]);
        }
        return normalized;
    }
    return value;
}
function valuesEqual(a, b) {
    return JSON.stringify(normalizeValue(a)) === JSON.stringify(normalizeValue(b));
}
/**
 * A stable content key for a value: object keys are sorted recursively before
 * serialising, so two values differing only in object-key order produce the
 * same key. Array element order is preserved (and therefore significant),
 * matching {@link valuesEqual}. Used by the pattern diff to match id-less items
 * by content.
 */
export function canonicalKey(value) {
    return JSON.stringify(normalizeValue(value));
}
function omitUniqueId(item) {
    const { 'unique-id': _omit, ...rest } = item;
    void _omit;
    return rest;
}
export function nodeStructureMatches(a, b) {
    return valuesEqual(omitUniqueId(a), omitUniqueId(b));
}
export function relationshipStructureMatches(a, b) {
    return valuesEqual(omitUniqueId(a), omitUniqueId(b));
}
function diffControlItem(controlItemA, controlItemB) {
    const result = {
        descriptionDiff: diffSentences(controlItemA.description, controlItemB.description).map((diff) => ({
            content: diff.value,
            changeType: getChangeTypeFromChangeObject(diff),
        })),
        requirementsDiff: diffArrays(controlItemA.requirements, controlItemB.requirements, { comparator: valuesEqual }).flatMap((diff) => diff.value.map((val) => ({
            content: val,
            changeType: getChangeTypeFromChangeObject(diff),
        })))
    };
    return result;
}
function stringifyMetadata(metadata) {
    if (Array.isArray(metadata)) {
        return metadata.map((obj) => JSON.stringify(normalizeValue(obj)));
    }
    else {
        return [JSON.stringify(normalizeValue(metadata))];
    }
}
function isEmptyMetadata(metadata) {
    return Array.isArray(metadata) ? metadata.length === 0 : Object.keys(metadata).length === 0;
}
function metadataAsItems(metadata) {
    return Array.isArray(metadata)
        ? metadata.map((obj) => normalizeValue(obj))
        : [normalizeValue(metadata)];
}
function diffMetadataObject(metadataObjA, metadataObjB) {
    const result = {};
    Object.keys(metadataObjA).forEach((key) => {
        if (!Object.prototype.hasOwnProperty.call(metadataObjB, key)) {
            result[key] = {
                changeType: 'removed',
                oldValue: metadataObjA[key],
                newValue: null
            };
        }
        else if (!valuesEqual(metadataObjA[key], metadataObjB[key])) {
            result[key] = {
                changeType: 'modified',
                oldValue: metadataObjA[key],
                newValue: metadataObjB[key]
            };
        }
    });
    Object.keys(metadataObjB).forEach((key) => {
        if (!Object.prototype.hasOwnProperty.call(metadataObjA, key)) {
            result[key] = {
                changeType: 'added',
                oldValue: null,
                newValue: metadataObjB[key]
            };
        }
    });
    return result;
}
function getChangeTypeFromChangeObject(changeObject) {
    return changeObject.added ? 'added' : changeObject.removed ? 'removed' : 'unchanged';
}
/**
 * Generates the diff between two CALM architecture instances.
 * @param archA Architecture "before", on which changes are to be made
 * @param archB Arhcitecture "after" which is changed with respect to architecture A
 * @returns Object representing diffs across different properties of the CALM schema.
 */
export function diffArchitectures(archA, archB) {
    const nodesAndRelationshipsDiff = diffNodesAndRelationships(archA.nodes ?? [], archB.nodes ?? [], archA.relationships ?? [], archB.relationships ?? []);
    const adrDiff = diffAdrs(archA.adrs ?? [], archB.adrs ?? []);
    const controlsDiff = diffControls(archA.controls ?? {}, archB.controls ?? {});
    const metadataDiff = diffMetadata(archA.metadata ?? {}, archB.metadata ?? {});
    return {
        ...nodesAndRelationshipsDiff,
        ...adrDiff,
        ...controlsDiff,
        ...metadataDiff
    };
}
/**
 * Core diff over the node/relationship arrays that both architectures and
 * (normalised) patterns reduce to. Matching is by `unique-id`; items missing
 * one are surfaced via `invalidItems` rather than silently dropped.
 */
export function diffNodesAndRelationships(allNodesA, allNodesB, allEdgesA, allEdgesB) {
    const validNodesA = allNodesA.filter((n) => n['unique-id']);
    const validNodesB = allNodesB.filter((n) => n['unique-id']);
    const validEdgesA = allEdgesA.filter((r) => r['unique-id']);
    const validEdgesB = allEdgesB.filter((r) => r['unique-id']);
    const invalidNodes = [
        ...allNodesA.filter((n) => !n['unique-id']),
        ...allNodesB.filter((n) => !n['unique-id']),
    ];
    const invalidEdges = [
        ...allEdgesA.filter((r) => !r['unique-id']),
        ...allEdgesB.filter((r) => !r['unique-id']),
    ];
    const nodesA = new Map(validNodesA.map((n) => [n['unique-id'], n]));
    const nodesB = new Map(validNodesB.map((n) => [n['unique-id'], n]));
    const edgesA = new Map(validEdgesA.map((r) => [r['unique-id'], r]));
    const edgesB = new Map(validEdgesB.map((r) => [r['unique-id'], r]));
    const nodesRemovedList = [...nodesA.values()].filter((n) => !nodesB.has(n['unique-id']));
    const nodesAddedList = [...nodesB.values()].filter((n) => !nodesA.has(n['unique-id']));
    const nodesRenamed = [];
    const nodesRemovedUnmatched = new Set(nodesRemovedList.map((n) => n['unique-id']));
    const nodesAddedUnmatched = new Set(nodesAddedList.map((n) => n['unique-id']));
    for (const removed of nodesRemovedList) {
        for (const added of nodesAddedList) {
            const addedId = added['unique-id'];
            if (!nodesAddedUnmatched.has(addedId))
                continue;
            if (nodeStructureMatches(removed, added)) {
                nodesRenamed.push({
                    oldId: removed['unique-id'],
                    newId: addedId,
                    node: added,
                });
                nodesRemovedUnmatched.delete(removed['unique-id']);
                nodesAddedUnmatched.delete(addedId);
                break;
            }
        }
    }
    const edgesRemovedList = [...edgesA.values()].filter((e) => !edgesB.has(e['unique-id']));
    const edgesAddedList = [...edgesB.values()].filter((e) => !edgesA.has(e['unique-id']));
    const edgesRenamed = [];
    const edgesRemovedUnmatched = new Set(edgesRemovedList.map((e) => e['unique-id']));
    const edgesAddedUnmatched = new Set(edgesAddedList.map((e) => e['unique-id']));
    for (const removed of edgesRemovedList) {
        for (const added of edgesAddedList) {
            const addedId = added['unique-id'];
            if (!edgesAddedUnmatched.has(addedId))
                continue;
            if (relationshipStructureMatches(removed, added)) {
                edgesRenamed.push({
                    oldId: removed['unique-id'],
                    newId: addedId,
                    relationship: added,
                });
                edgesRemovedUnmatched.delete(removed['unique-id']);
                edgesAddedUnmatched.delete(addedId);
                break;
            }
        }
    }
    const nodesModified = [];
    const nodesSame = [];
    for (const [id, nodeA] of nodesA) {
        if (!nodesB.has(id))
            continue;
        const nodeB = nodesB.get(id);
        if (valuesEqual(nodeA, nodeB)) {
            nodesSame.push(nodeA);
        }
        else {
            nodesModified.push({ original: nodeA, updated: nodeB });
        }
    }
    const edgesModified = [];
    const edgesSame = [];
    for (const [id, edgeA] of edgesA) {
        if (!edgesB.has(id))
            continue;
        const edgeB = edgesB.get(id);
        if (valuesEqual(edgeA, edgeB)) {
            edgesSame.push(edgeA);
        }
        else {
            edgesModified.push({ original: edgeA, updated: edgeB });
        }
    }
    return {
        nodesAdded: [...nodesAddedUnmatched].map((id) => nodesB.get(id)),
        nodesRemoved: [...nodesRemovedUnmatched].map((id) => nodesA.get(id)),
        nodesModified,
        nodesSame,
        nodesRenamed,
        edgesAdded: [...edgesAddedUnmatched].map((id) => edgesB.get(id)),
        edgesRemoved: [...edgesRemovedUnmatched].map((id) => edgesA.get(id)),
        edgesModified,
        edgesSame,
        edgesRenamed,
        invalidItems: {
            nodes: invalidNodes,
            relationships: invalidEdges,
        },
    };
}
/**
 * Core diff function for ADR arrays (each ADR is a string, typically a URL). Identifies which ADRs were added, removed or unchanged between two arrays.
 */
export function diffAdrs(adrsA = [], adrsB = []) {
    return {
        adrDiffItems: diffArrays(adrsA, adrsB).flatMap((diff) => diff.value.map((val) => ({
            content: val,
            changeType: getChangeTypeFromChangeObject(diff),
        })))
    };
}
/**
 * Core diff function for CALM control objects. Identifies which controls are added, removed and unchanged by ID, and identifies control changes under an individual ID.
 */
export function diffControls(controlsA, controlsB) {
    const result = {
        controlItemsAdded: {},
        controlItemsRemoved: {},
        controlItemsUnchanged: {},
        controlItemsModified: {}
    };
    const commonControlIds = [];
    Object.keys(controlsA).forEach((id) => {
        if (controlsB[id] != null) {
            commonControlIds.push(id);
        }
        else {
            result.controlItemsRemoved[id] = controlsA[id];
        }
    });
    Object.keys(controlsB).forEach((id) => {
        if (controlsA[id] == null) {
            result.controlItemsAdded[id] = controlsB[id];
        }
    });
    const editedControlIds = [];
    commonControlIds.forEach((id) => {
        if (valuesEqual(controlsA[id], controlsB[id])) {
            result.controlItemsUnchanged[id] = controlsA[id];
        }
        else {
            editedControlIds.push(id);
        }
    });
    editedControlIds.forEach((id) => {
        result.controlItemsModified[id] = diffControlItem(controlsA[id], controlsB[id]);
    });
    return result;
}
/**
 * Core diff function for CALM metadata objects/arrays of objects. For objects, identifies what fields were added, removed or updated. For arrays, identifies what objects were added, removed or unchanged.
 * Object-form and array-form metadata are distinct shapes (`oneOf` in the CALM schema), so a change
 * of form is reported as a full removal/addition rather than being content-diffed across the boundary.
 * An empty side (missing metadata, `{}` or `[]`) contributes nothing rather than being diffed as a value.
 */
export function diffMetadata(metadataA, metadataB) {
    const result = {
        metadataObjectsAdded: [],
        metadataObjectsRemoved: [],
        metadataObjectsUnchanged: [],
        metadataObjectsModified: [],
    };
    const isEmptyA = isEmptyMetadata(metadataA);
    const isEmptyB = isEmptyMetadata(metadataB);
    if (isEmptyA && isEmptyB) {
        return result;
    }
    if (isEmptyA !== isEmptyB) {
        if (isEmptyA) {
            result.metadataObjectsAdded.push(...metadataAsItems(metadataB));
        }
        else {
            result.metadataObjectsRemoved.push(...metadataAsItems(metadataA));
        }
        return result;
    }
    if (Array.isArray(metadataA) !== Array.isArray(metadataB)) {
        result.metadataObjectsRemoved.push(...metadataAsItems(metadataA));
        result.metadataObjectsAdded.push(...metadataAsItems(metadataB));
        return result;
    }
    if (!Array.isArray(metadataA) && !Array.isArray(metadataB)) {
        if (valuesEqual(metadataA, metadataB)) {
            result.metadataObjectsUnchanged.push(normalizeValue(metadataA));
        }
        else {
            result.metadataObjectsModified.push(diffMetadataObject(metadataA, metadataB));
        }
        return result;
    }
    const stringifiedArrayA = stringifyMetadata(metadataA);
    const stringifiedArrayB = stringifyMetadata(metadataB);
    const stringifiedDiffItems = diffArrays(stringifiedArrayA, stringifiedArrayB);
    for (let i = 0; i < stringifiedDiffItems.length; i++) {
        const diffItem = stringifiedDiffItems[i];
        if (diffItem.added) {
            diffItem.value.forEach((str) => result.metadataObjectsAdded.push(JSON.parse(str)));
        }
        else if (diffItem.removed) {
            diffItem.value.forEach((str) => result.metadataObjectsRemoved.push(JSON.parse(str)));
        }
        else {
            diffItem.value.forEach((str) => result.metadataObjectsUnchanged.push(JSON.parse(str)));
        }
    }
    return result;
}
//# sourceMappingURL=diff.js.map